import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge Tailwind CSS classes with clsx
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Generate preview URL from deployment details
 */
export function getPreviewUrl(s3Prefix: string, baseDomain?: string): string {
  const domain = baseDomain || import.meta.env.VITE_BASE_DOMAIN || 'example.com';
  // Extract deployment ID from s3_prefix (format: "deploys/<deployment_id>")
  const deploymentId = s3Prefix.split('/').pop() || s3Prefix;
  return `https://${deploymentId}.${domain}`;
}

/**
 * Generate production URL from project name
 */
export function getProductionUrl(projectName: string, baseDomain?: string): string {
  const domain = baseDomain || import.meta.env.VITE_BASE_DOMAIN || 'example.com';
  return `https://${projectName}.${domain}`;
}

/**
 * Format deployment status for display
 */
export function formatStatus(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/**
 * Get status color class
 */
export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    pending: 'text-yellow-600 bg-yellow-50',
    building: 'text-blue-600 bg-blue-50',
    success: 'text-green-600 bg-green-50',
    failed: 'text-red-600 bg-red-50',
  };
  return colors[status] || 'text-gray-600 bg-gray-50';
}

/**
 * Truncate string to specified length
 */
export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
}

/**
 * Format commit SHA (first 7 characters)
 */
export function formatCommitSha(sha: string): string {
  return sha.slice(0, 7);
}

/**
 * Format build time
 */
export function formatBuildTime(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

/**
 * Validate GitHub URL
 */
export function isValidGitHubUrl(url: string): boolean {
  const githubUrlPattern = /^https?:\/\/(www\.)?github\.com\/[\w-]+\/[\w.-]+\/?$/;
  return githubUrlPattern.test(url);
}

/**
 * Extract repo name from GitHub URL
 */
export function extractRepoName(url: string): string {
  const match = url.match(/github\.com\/([\w-]+)\/([\w.-]+)/);
  return match ? match[2].replace('.git', '') : '';
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
}

/**
 * Format bytes to human-readable size
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}
